import {
  BifrostChunk,
  RpcEnvelope,
  RpcResponse,
  V2BootstrapAdapter,
  V2ControlFrame,
  V2Hello,
  V2SessionGrant,
} from "../types/protocol";
import { ChunkAssembler } from "./ChunkAssembler";
import { splitIntoChunks, utf8ByteLength } from "./ChunkSender";
import { PayloadTooLargeError } from "../types/errors";

let fallbackId = 0;

function freshId(): string {
  const crypto = globalThis.crypto;
  if (crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      "",
    );
  }
  fallbackId += 1;
  return `document-${Date.now()}-${fallbackId}`;
}

export class V2Session {
  private grant?: V2SessionGrant;
  private detachReceiver?: () => void;
  private ready?: Promise<boolean>;
  private resolveReady?: (ready: boolean) => void;
  private state: "idle" | "arming" | "ready" | "faulted" | "closed" = "idle";
  private multipart?: ChunkAssembler;

  constructor(
    private readonly adapter: V2BootstrapAdapter,
    private readonly onMessage: (message: RpcEnvelope | RpcResponse) => void,
    private readonly onFault: () => void = () => undefined,
    private readonly onChunkAccepted: (chunk: BifrostChunk) => void = () =>
      undefined,
  ) {}

  initialize(timeoutMs: number): Promise<boolean> {
    if (this.ready) return this.ready;
    this.ready = this.negotiate(timeoutMs);
    return this.ready;
  }

  isReady(): boolean {
    return this.state === "ready";
  }

  directLimit(): number {
    return this.grant?.limits.maxDirectFrameBytes ?? 0;
  }

  multipartChunkSize(): number {
    return this.grant?.limits.multipartChunkBytes ?? 128 * 1024;
  }

  admissionWindow(): number {
    return this.grant?.limits.admissionWindow ?? 0;
  }

  send(message: RpcEnvelope | RpcResponse): "admitted" | "not_admitted" {
    if (!this.grant || this.state !== "ready")
      throw new Error("V2 session is not ready");
    const kind = "kind" in message ? message.kind : "response";
    const serialized = JSON.stringify(message);
    const sizeBytes = utf8ByteLength(serialized);
    if (sizeBytes > this.directLimit()) {
      const limits = this.grant.limits;
      if (
        !this.grant.features.includes("multipart") ||
        !limits.maxMultipartBytes ||
        !limits.multipartChunkBytes ||
        sizeBytes > limits.maxMultipartBytes
      )
        throw new PayloadTooLargeError();
      const transferId = freshId();
      for (const chunk of splitIntoChunks(
        transferId,
        serialized,
        limits.multipartChunkBytes,
        "id" in message ? message.id : undefined,
      )) {
        if (
          this.adapter.send(
            JSON.stringify({ ...this.binding("multipart_chunk"), chunk }),
          ) === "not_admitted"
        )
          return "not_admitted";
      }
      return "admitted";
    }
    return (
      this.adapter.send(
        JSON.stringify({ ...this.binding(kind), ...message, kind }),
      ) ?? "admitted"
    );
  }

  close(): void {
    if (this.state === "closed") return;
    this.state = "closed";
    this.resolveReady?.(false);
    this.detachReceiver?.();
    this.detachReceiver = undefined;
    this.grant = undefined;
    this.multipart?.destroy();
  }

  recover(timeoutMs: number): Promise<boolean> {
    if (this.state !== "faulted") return this.initialize(timeoutMs);
    this.ready = undefined;
    this.resolveReady = undefined;
    this.grant = undefined;
    this.multipart?.destroy();
    this.multipart = undefined;
    this.state = "idle";
    return this.initialize(timeoutMs);
  }

  private async negotiate(timeoutMs: number): Promise<boolean> {
    const hello: V2Hello = {
      kind: "hello",
      documentNonce: freshId(),
      handshakeAttempt: freshId(),
      protocols: [2],
      features: ["multipart"],
      webToNativeTransports: ["ios_wk_reply", "android_js_interface"],
      nativeToWebTransports: ["ios_reply_rearm", "android_web_message_port"],
      representations: ["base64"],
      limits: {
        maxDirectFrameBytes: 262_144,
        admissionWindow: 8,
        controlPriorityWindow: 2,
        maxMultipartBytes: 16 * 1024 * 1024,
        multipartChunkBytes: 64 * 1024,
        maxActiveBulkTransfers: 2,
      },
    };
    const outcome = await this.adapter.hello(hello);
    if (outcome.kind === "session_rejected" || !this.validGrant(outcome))
      return false;
    this.grant = outcome;
    if (outcome.features.includes("multipart")) {
      this.multipart = new ChunkAssembler({
        maxConcurrentTransfers: outcome.limits.maxActiveBulkTransfers,
        maxTransferBytes: outcome.limits.maxMultipartBytes,
        maxTotalBytes:
          outcome.limits.maxMultipartBytes! *
          outcome.limits.maxActiveBulkTransfers!,
        onAccept: (chunk) => this.onChunkAccepted(chunk),
      });
    }
    this.state = "arming";
    this.detachReceiver = this.adapter.arm((frame) => this.receive(frame));
    const readyAck = new Promise<boolean>((resolve) => {
      this.resolveReady = resolve;
    });
    const timeout = setTimeout(() => this.resolveReady?.(false), timeoutMs);
    if (
      this.adapter.send(JSON.stringify(this.binding("session_ready"))) ===
      "not_admitted"
    ) {
      this.close();
      return false;
    }
    const ready = await readyAck;
    clearTimeout(timeout);
    if (!ready) this.close();
    return ready;
  }

  private receive(frameText: string): void {
    if (!this.grant || this.state === "closed" || this.state === "faulted")
      return;
    let frame: V2ControlFrame;
    try {
      const parsed: unknown = JSON.parse(frameText);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        return;
      frame = parsed as V2ControlFrame;
    } catch {
      return;
    }
    if (
      frame.protocol !== 2 ||
      frame.sessionId !== this.grant.sessionId ||
      frame.capability !== this.grant.capability
    )
      return;
    if (frame.kind === "session_ready_ack" && this.state === "arming") {
      this.state = "ready";
      this.resolveReady?.(true);
      return;
    }
    if (this.state !== "ready") return;
    if (frame.kind === "multipart_chunk") {
      const responseId = frame.chunk?.responseId;
      const assembled = frame.chunk && this.multipart?.addChunk(frame.chunk);
      if (!assembled) return;
      let application: RpcEnvelope | RpcResponse;
      try {
        application = JSON.parse(assembled);
      } catch {
        return this.fault();
      }
      if (responseId !== undefined && application.id !== responseId) {
        return this.fault();
      }
      this.receiveApplication(application);
    } else if (frame.kind === "call" || frame.kind === "notify") {
      if (!frame.id || !frame.service || !frame.method) return this.fault();
      this.onMessage({
        id: frame.id,
        kind: frame.kind,
        service: frame.service,
        method: frame.method,
        payload: frame.payload,
        ts: typeof frame.ts === "number" ? frame.ts : Date.now(),
      });
    } else if (frame.kind === "response") {
      if (!frame.id || typeof frame.ok !== "boolean") return this.fault();
      this.onMessage({
        id: frame.id,
        ok: frame.ok,
        result: frame.result,
        error: frame.error as RpcResponse["error"],
        ts: typeof frame.ts === "number" ? frame.ts : Date.now(),
      });
    } else {
      this.fault();
    }
  }

  private receiveApplication(message: RpcEnvelope | RpcResponse): void {
    if ("kind" in message) {
      if (
        (message.kind !== "call" && message.kind !== "notify") ||
        !message.id ||
        !message.service ||
        !message.method
      )
        return this.fault();
    } else if (!message.id || typeof message.ok !== "boolean") {
      return this.fault();
    }
    this.onMessage(message);
  }

  private binding(kind: V2ControlFrame["kind"]): V2ControlFrame {
    return {
      protocol: 2,
      sessionId: this.grant!.sessionId,
      capability: this.grant!.capability,
      kind,
    };
  }

  private validGrant(grant: V2SessionGrant): boolean {
    return (
      grant.protocol === 2 &&
      grant.sessionId.length > 0 &&
      grant.capability.length >= 22 &&
      Array.isArray(grant.features) &&
      ["ios_wk_reply", "android_js_interface"].includes(
        grant.webToNativeTransport,
      ) &&
      ["ios_reply_rearm", "android_web_message_port"].includes(
        grant.nativeToWebTransport,
      ) &&
      Number.isSafeInteger(grant.limits.maxDirectFrameBytes) &&
      grant.limits.maxDirectFrameBytes > 0 &&
      Number.isSafeInteger(grant.limits.admissionWindow) &&
      grant.limits.admissionWindow > 0 &&
      Number.isSafeInteger(grant.limits.controlPriorityWindow) &&
      grant.limits.controlPriorityWindow > 0 &&
      (!grant.features.includes("multipart") ||
        (Number.isSafeInteger(grant.limits.maxMultipartBytes) &&
          grant.limits.maxMultipartBytes! > grant.limits.maxDirectFrameBytes &&
          Number.isSafeInteger(grant.limits.multipartChunkBytes) &&
          grant.limits.multipartChunkBytes! > 0 &&
          Number.isSafeInteger(grant.limits.maxActiveBulkTransfers) &&
          grant.limits.maxActiveBulkTransfers! > 0))
    );
  }

  private fault(): void {
    if (this.state === "faulted" || this.state === "closed") return;
    this.state = "faulted";
    this.detachReceiver?.();
    this.detachReceiver = undefined;
    this.onFault();
  }
}
